import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  FlaskConical,
  History,
  Loader2,
  Plus,
  Settings2,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import type { AgentComponent, WorkflowDetail, WorkflowGraph, WorkflowStep } from '@vagusx/clients';
import { stepTestStandsInIdentity } from '@vagusx/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useComponentCatalog,
  useWorkflow,
  useCreateStep,
  useUpdateWorkflow,
  useUpdateStep,
} from '@/hooks/use-workflows';
import { useOpsMode } from '@/hooks/use-ops-mode';
import { WorkflowSettings, type WorkflowPatch } from './WorkflowSettings';
import { AddStepDialog } from './AddStepDialog';
import { TestCallSheet, type TestTarget } from './TestCallSheet';
import type { EditorView } from './search';
import { PublishDialog } from './PublishDialog';
import { Canvas } from './Canvas';
import { ConnectDialog, type Connection } from './ConnectDialog';
import type { ArrowSlot } from './NextSteps';
import { StepSettings, type StepPatch } from './StepSettings';
import {
  canLeadTo,
  defaultExits,
  endingExits,
  insertAfter,
  insertAsTarget,
  insertBetween,
  newNodeId,
  nextOf,
  problemsByNode,
  removeNode,
  setOtherwise,
  stepExits,
  strandedByRemoving,
  withoutDefaultWhereEnding,
} from './graph';

/**
 * Where "Add a step" puts the new step: on the arrow between two steps, at the
 * end of one of a step's arrows, or straight after a step (on its own when
 * nothing is selected).
 */
type AddAt =
  | { kind: 'between'; from: string; to: string }
  | { kind: 'arrow'; from: string; slot: ArrowSlot }
  | { kind: 'after'; after: string | null };

/**
 * The workflow builder: the call as steps and arrows (middle), and the
 * selected step or the workflow itself (right).
 *
 * EVERY EDIT IS SAVED AS IT IS MADE. The draft lives on the server, which
 * answers each save with the whole workflow — problems and gate facts
 * recomputed — so nothing here re-derives what the engine would do. Nothing
 * reaches a call until Publish; "Unpublished changes" says when the draft and
 * the live version differ.
 */
export function WorkflowEditor({
  id,
  view,
  onView,
  onClose,
}: {
  id: string;
  /** The selected step and the open test, from the URL. */
  view: EditorView;
  /** Change them — by changing the URL, so each is a link. */
  onView: (change: Partial<EditorView>) => void;
  onClose: () => void;
}): React.ReactElement {
  const { data: detail, isLoading, error } = useWorkflow(id);
  const { data: catalog } = useComponentCatalog();

  if (error) {
    return (
      <div className="p-6 text-sm text-destructive">
        Could not load this workflow.{' '}
        <Button variant="link" onClick={onClose}>
          Back
        </Button>
      </div>
    );
  }
  if (isLoading || !detail || !catalog) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  return <Editor detail={detail} catalog={catalog} view={view} onView={onView} onClose={onClose} />;
}

function Editor({
  detail,
  catalog,
  view,
  onView,
  onClose,
}: {
  detail: WorkflowDetail;
  catalog: NonNullable<ReturnType<typeof useComponentCatalog>['data']>;
  view: EditorView;
  onView: (change: Partial<EditorView>) => void;
  onClose: () => void;
}): React.ReactElement {
  const ops = useOpsMode();
  const update = useUpdateWorkflow(detail.id);
  const createStep = useCreateStep(detail.id);
  const updateStep = useUpdateStep(detail.id);
  const busy = update.isPending || createStep.isPending || updateStep.isPending;

  // The selected step lives in the URL. A node not (yet) in the draft
  // selects nothing: a step just added is selected before its save returns.
  const selectedId = view.step;
  const setSelectedId = (nodeId: string | null): void => onView({ step: nodeId });
  /** Where "Add a step" puts the new step; `null` is the dialog closed. */
  const [addAt, setAddAt] = useState<AddAt | null>(null);
  const [connecting, setConnecting] = useState<Connection | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  /**
   * Whether the test opening now dials at once. Pressing Test does; a test
   * opened any other way (a link, a reload, Back or Forward) waits for Start,
   * so a URL never dials on its own.
   */
  const [autoStart, setAutoStart] = useState(false);
  const startTest = (change: Partial<EditorView>): void => {
    setAutoStart(true);
    onView(change);
  };
  const testOpen = view.test !== null;
  useEffect(() => {
    if (!testOpen) setAutoStart(false);
  }, [testOpen]);

  const steps = useMemo(() => new Map(detail.steps.map((s) => [s.id, s])), [detail.steps]);
  const components = useMemo(
    () => new Map(catalog.components.map((c) => [c.key, c])),
    [catalog.components]
  );

  const testNode =
    view.test === 'step' ? detail.draft.nodes.find((n) => n.id === view.step) : undefined;
  const testStep = testNode && steps.get(testNode.stepId);
  const testing: TestTarget | null =
    view.test === 'workflow'
      ? { workflowId: detail.id, workflowName: detail.name }
      : testNode && testStep
        ? {
            workflowId: detail.id,
            workflowName: detail.name,
            step: {
              nodeId: testNode.id,
              name: testStep.name,
              standsInIdentity: stepTestStandsInIdentity(
                components.get(testStep.componentKey)?.meta.category
              ),
            },
          }
        : null;
  // A link to a step that is no longer in the draft.
  const staleStepTest = view.test === 'step' && !testing;
  useEffect(() => {
    if (!staleStepTest) return;
    // An id, so a double-run effect (StrictMode) shows it once.
    toast.error('That step is no longer in this workflow, so there is nothing to test.', {
      id: 'stale-step-test',
    });
    onView({ step: null, test: null });
  }, [staleStepTest, onView]);
  const problems = useMemo(() => problemsByNode(detail.problemList), [detail.problemList]);
  const selected = detail.draft.nodes.find((n) => n.id === selectedId) ?? null;
  const selectedStep = selected ? steps.get(selected.stepId) : undefined;
  const target = { channel: detail.channel, direction: detail.direction };
  const errors = detail.problems.errors;
  const warnings = detail.problems.warnings;

  const saveGraph = (draft: WorkflowGraph, then?: () => void): void =>
    update.mutate(
      { expectedRevision: detail.revision, draft },
      then ? { onSuccess: () => then() } : undefined
    );

  const saveWorkflow = (patch: WorkflowPatch): void => {
    const { close, closeWhen, anyTime, ...fields } = patch;
    let draft: WorkflowGraph | undefined;
    if (close !== undefined || closeWhen !== undefined || anyTime !== undefined) {
      draft = { ...detail.draft };
      if (close === null) delete draft.close;
      else if (close !== undefined) draft.close = close;
      if (closeWhen === null) delete draft.closeWhen;
      else if (closeWhen !== undefined) draft.closeWhen = closeWhen;
      if (anyTime === null) delete draft.anyTime;
      else if (anyTime !== undefined) draft.anyTime = anyTime;
    }
    update.mutate({ expectedRevision: detail.revision, ...fields, ...(draft ? { draft } : {}) });
  };

  const stepName = (nodeId: string): string => {
    const node = detail.draft.nodes.find((n) => n.id === nodeId);
    return (node && steps.get(node.stepId)?.name) ?? 'this step';
  };

  /** Put a node running `stepId` where `addAt` says, and select it. */
  const place = (stepId: string, componentKey: string, then?: () => void): void => {
    if (!addAt) return;
    const node = { id: newNodeId(detail.draft, componentKey), stepId };
    const placed =
      addAt.kind === 'between'
        ? insertBetween(detail.draft, addAt.from, addAt.to, node)
        : addAt.kind === 'arrow'
          ? insertAsTarget(detail.draft, addAt.from, addAt.slot, node)
          : insertAfter(detail.draft, addAt.after, node);
    const exits = stepExits(
      { componentKey, config: {} },
      components.get(componentKey),
      catalog.decisionPresets
    );
    saveGraph(withoutDefaultWhereEnding(placed, node.id, exits), then);
    setSelectedId(node.id);
  };

  const addComponent = (component: AgentComponent, preset?: string): void => {
    createStep.mutate(
      { componentKey: component.key, name: component.meta.title },
      {
        onSuccess: (step) => {
          // A decision starts from its component's default question; the one
          // picked in the dialog replaces it, and names the step after it.
          //
          // AFTER the graph save, never beside it: a step save refetches the
          // workflow, and a refetch that read the database before the graph
          // save committed would land after it and roll the cache back to a
          // workflow without the new step.
          const question = catalog.decisionPresets.find((p) => p.key === preset);
          place(
            step.id,
            component.key,
            question
              ? () =>
                  updateStep.mutate({
                    stepId: step.id,
                    body: {
                      expectedRevision: step.revision,
                      name: question.config.question,
                      config: { ...step.config, preset: question.key },
                    },
                  })
              : undefined
          );
        },
      }
    );
  };

  const addShared = (step: WorkflowStep): void => place(step.id, step.componentKey);

  const addPosition = ((): string => {
    if (!addAt) return '';
    if (addAt.kind === 'between') {
      return `between “${stepName(addAt.from)}” and “${stepName(addAt.to)}”`;
    }
    if (addAt.kind === 'arrow') return `after “${stepName(addAt.from)}”, on that path`;
    return addAt.after ? `straight after “${stepName(addAt.after)}”` : 'on its own, to connect';
  })();

  /** An arrow dragged from one step to another. */
  const connect = (from: string, to: string): void => {
    if (!canLeadTo(detail.draft, from, to)) {
      toast.error('Arrows only go forward in the call. That step already comes before this one.');
      return;
    }
    const node = detail.draft.nodes.find((n) => n.id === from);
    const step = node && steps.get(node.stepId);
    const exits = stepExits(
      step,
      components.get(step?.componentKey ?? ''),
      catalog.decisionPresets
    );
    // A step that ends the call on purpose on some endings (a transfer that
    // went through) never gets a default: that would carry the call on past
    // the transfer. The arrow can only be for one of its other endings.
    if (node && !nextOf(node).otherwise && endingExits(node, exits).length > 0) {
      if (defaultExits(node, exits).length === 0) {
        const ends = endingExits(node, exits)
          .map((e) => `“${e.label}”`)
          .join(' or ');
        toast.error(
          `Every other way this step can end already has an arrow. On ${ends}, the call ends here.`
        );
        return;
      }
      setConnecting({ from, to });
      return;
    }
    // With one way out, or no default yet, the arrow simply becomes the default.
    if (!node || !nextOf(node).otherwise || defaultExits(node, exits).length < 2) {
      saveGraph(setOtherwise(detail.draft, from, to));
      return;
    }
    setConnecting({ from, to });
  };

  const saveStep = (step: WorkflowStep, patch: StepPatch): void =>
    updateStep.mutate({ stepId: step.id, body: { expectedRevision: step.revision, ...patch } });

  const shared = detail.steps.filter(
    (s) => s.ownerWorkflowId === null && !detail.draft.nodes.some((n) => n.stepId === s.id)
  );
  const nodesUsingSelectedStep = selected
    ? detail.draft.nodes.filter((n) => n.stepId === selected.stepId).length
    : 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-lg font-semibold">{detail.name}</h1>
            {detail.activeVersion ? (
              <Badge variant="secondary">Live: version {detail.activeVersion.version}</Badge>
            ) : (
              <Badge variant="outline">Not published</Badge>
            )}
            {detail.hasUnpublishedChanges && detail.activeVersion && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-primary" />
                Unpublished changes
              </span>
            )}
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          <p className="truncate text-xs text-muted-foreground">
            {errors > 0
              ? `${errors} problem${errors === 1 ? '' : 's'} to fix before publishing`
              : 'Ready to publish'}
            {warnings > 0 && ` · ${warnings} warning${warnings === 1 ? '' : 's'}`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => setAddAt({ kind: 'after', after: selectedId })}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add step
        </Button>
        <Button
          variant={selectedId === null ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setSelectedId(null)}
        >
          <Settings2 className="mr-2 h-4 w-4" />
          Workflow
          {(problems.get('')?.errors.length ?? 0) > 0 && (
            <TriangleAlert className="ml-1.5 h-3.5 w-3.5 text-destructive" />
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" disabled={detail.versions.length === 0}>
              <History className="mr-2 h-4 w-4" />
              Versions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>Published versions</DropdownMenuLabel>
            {detail.versions.map((v) => (
              <DropdownMenuItem key={v.id} className="flex flex-col items-start" disabled>
                <span className="text-sm">
                  Version {v.version}: {v.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {v.createdAt.toLocaleString()}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => startTest({ test: 'workflow' })}
        >
          <FlaskConical className="mr-2 h-4 w-4" />
          Test
        </Button>
        <Button onClick={() => setPublishOpen(true)} disabled={busy}>
          <Upload className="mr-2 h-4 w-4" />
          Publish
        </Button>
      </header>

      <div className="flex min-h-0 flex-1">
        <main className="min-w-0 flex-1 bg-muted/20">
          <Canvas
            graph={detail.draft}
            steps={steps}
            components={components}
            presets={catalog.decisionPresets}
            callFacts={catalog.callFacts}
            problems={problems}
            selectedId={selectedId}
            busy={busy}
            fitKey={detail.id}
            onSelect={setSelectedId}
            onInsert={(from, to) => setAddAt({ kind: 'between', from, to })}
            onConnect={connect}
          />
        </main>
        <aside className="w-136 shrink-0 overflow-y-auto border-l xl:w-160">
          {selected && selectedStep ? (
            <StepSettings
              key={selected.id}
              node={selected}
              step={selectedStep}
              component={components.get(selectedStep.componentKey)}
              catalog={catalog}
              direction={detail.direction}
              factsBefore={detail.factsBefore[selected.id] ?? []}
              problems={[
                ...(problems.get(selected.id)?.errors ?? []),
                ...(problems.get(selected.id)?.warnings ?? []),
              ]}
              ops={ops}
              busy={busy}
              usedElsewhere={nodesUsingSelectedStep > 1 || selectedStep.ownerWorkflowId === null}
              graph={detail.draft}
              steps={steps}
              onSaveStep={(patch) => saveStep(selectedStep, patch)}
              onSaveGraph={saveGraph}
              onNewStep={(slot) => setAddAt({ kind: 'arrow', from: selected.id, slot })}
              onRemove={
                selected.id === detail.draft.entry
                  ? undefined
                  : () => {
                      const stranded = strandedByRemoving(detail.draft, selected.id);
                      if (stranded.length) {
                        const names = stranded
                          .map((n) => `“${steps.get(n.stepId)?.name ?? n.id}”`)
                          .join(', ');
                        toast.error(
                          `Nothing comes after this step, so ${names} would have nowhere to go and the call would end there. Point ${stranded.length === 1 ? 'it' : 'them'} somewhere else first.`
                        );
                        return;
                      }
                      saveGraph(removeNode(detail.draft, selected.id));
                      setSelectedId(null);
                    }
              }
              onTest={() => startTest({ step: selected.id, test: 'step' })}
              onClearGates={() =>
                saveGraph({
                  ...detail.draft,
                  nodes: detail.draft.nodes.map((n) => {
                    if (n.id !== selected.id) return n;
                    const { requires: _requires, unless: _unless, ...rest } = n;
                    return rest;
                  }),
                })
              }
            />
          ) : (
            <WorkflowSettings
              key={detail.revision}
              detail={detail}
              steps={steps}
              problems={[
                ...(problems.get('')?.errors ?? []),
                ...(problems.get('')?.warnings ?? []),
              ]}
              ops={ops}
              busy={busy}
              onSave={saveWorkflow}
            />
          )}
        </aside>
      </div>

      <AddStepDialog
        open={addAt !== null}
        onOpenChange={(open) => {
          if (!open) setAddAt(null);
        }}
        components={catalog.components}
        shared={shared}
        target={target}
        position={addPosition}
        onAdd={addComponent}
        onAddShared={addShared}
        presets={catalog.decisionPresets}
      />

      <ConnectDialog
        connection={connecting}
        graph={detail.draft}
        steps={steps}
        components={components}
        presets={catalog.decisionPresets}
        onClose={() => setConnecting(null)}
        onConnect={(draft) => {
          saveGraph(draft);
          if (connecting) setSelectedId(connecting.from);
          setConnecting(null);
        }}
      />

      <TestCallSheet
        target={testing}
        autoStart={autoStart}
        onClose={() => onView({ test: null })}
      />

      <PublishDialog
        detail={detail}
        steps={steps}
        open={publishOpen}
        onOpenChange={setPublishOpen}
      />
    </div>
  );
}
